import { AxiosError } from "axios";
import { httpTokenCommon } from "../helper/http-common";
import {
  ledgerEntriesFromPayments,
  normalizeLedgerResponse,
} from "../helper/walletLedgerUtils";
import { WalletLedgerEntry, WalletLedgerResponse } from "../types/wallet";
import invoiceService from "./InvoiceService";

/**
 * Wallet ledger service.
 * Primary: GET /api/customerswallets/transactions
 * Fallback/supplement: GET /api/payments (includes QiCard top-ups missing from unified API)
 */
class WalletService {
  private ledgerKey(entry: WalletLedgerEntry): string {
    return (entry.referenceId || entry.id || "").trim();
  }

  private mergeLedgerItems(
    primary: WalletLedgerEntry[],
    supplemental: WalletLedgerEntry[]
  ): WalletLedgerEntry[] {
    const seen = new Set(primary.map((entry) => this.ledgerKey(entry)).filter(Boolean));
    const merged = [...primary];

    for (const entry of supplemental) {
      const key = this.ledgerKey(entry);
      if (key && seen.has(key)) continue;
      merged.push(entry);
      if (key) seen.add(key);
    }

    return merged.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  private async fetchUnifiedLedger(): Promise<{
    found: boolean;
    data: WalletLedgerResponse;
    errorStatus?: number;
  }> {
    try {
      const res = await httpTokenCommon.get("customerswallets/transactions", {
        silent: true,
      });
      return { found: true, data: normalizeLedgerResponse(res.data) };
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 401 || status === 403) throw error;
      return { found: false, data: { items: [] }, errorStatus: status };
    }
  }

  private async fetchLegacyLedger(): Promise<WalletLedgerEntry[]> {
    const res = await invoiceService.getAllPayments().catch(() => ({ data: [] }));
    const rows = Array.isArray(res?.data) ? res.data : [];
    return ledgerEntriesFromPayments(rows);
  }

  async getLedger(): Promise<WalletLedgerResponse> {
    const [{ found, data, errorStatus }, legacyItems] = await Promise.all([
      this.fetchUnifiedLedger(),
      this.fetchLegacyLedger(),
    ]);

    if (!found) {
      return {
        items: legacyItems,
        usedLegacyFallback: true,
        unifiedErrorStatus: errorStatus,
      };
    }

    const mergedItems =
      data.items.length === 0
        ? legacyItems
        : this.mergeLedgerItems(data.items, legacyItems);

    return {
      items: mergedItems,
      balance: data.balance,
      currency: data.currency,
      usedLegacyFallback:
        legacyItems.length > 0 &&
        (data.items.length === 0 || mergedItems.length > data.items.length),
    };
  }
}

const walletService = new WalletService();
export default walletService;
